/**
 * Per-user native-call return context (Phase 4D).
 * sessionStorage only — never Firestore, tokens, or zegoBootstrap.
 */

const STORAGE_PREFIX = "livora:native-call-return:v1:";
const RETURN_AT_KEY = "livora:native-call-return-at:v1";
const EXPIRY_MS = 2 * 60 * 60 * 1000;
export const CALL_RETURN_FALLBACK_URL = "/home";

function safeSessionGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (_e) {
    return null;
  }
}

function safeSessionSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch (_e) {
    return false;
  }
}

function safeSessionRemove(key) {
  try {
    sessionStorage.removeItem(key);
  } catch (_e) {
    /* ignore */
  }
}

function storageKey(uid, callId) {
  return `${STORAGE_PREFIX}${String(uid || "")}:${String(callId || "")}`;
}

/**
 * Reject call routes and external URLs.
 */
export function isValidReturnUrl(url) {
  if (typeof url !== "string") return false;
  const trimmed = url.trim();
  if (!trimmed.startsWith("/")) return false;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)) return false;
  if (trimmed.includes("://")) return false;
  const pathOnly = trimmed.split(/[?#]/)[0] || "";
  if (pathOnly === "/call" || pathOnly.startsWith("/call/")) return false;
  if (pathOnly === "/call-native" || pathOnly.startsWith("/call-native/")) {
    return false;
  }
  if (pathOnly === "/call-outgoing" || pathOnly.startsWith("/call-outgoing/")) {
    return false;
  }
  return true;
}

export function buildReturnContextFromParts(pathname, search, hash) {
  const path = typeof pathname === "string" && pathname ? pathname : "/";
  const q = typeof search === "string" ? search : "";
  const h = typeof hash === "string" ? hash : "";
  const url = `${path}${q}${h}`;
  if (!isValidReturnUrl(url)) {
    console.log("[callReturn] invalid_return_url rejected=" + url);
    return null;
  }
  let scrollX = 0;
  let scrollY = 0;
  try {
    scrollX = Math.max(0, Math.round(window.scrollX || window.pageXOffset || 0));
    scrollY = Math.max(0, Math.round(window.scrollY || window.pageYOffset || 0));
  } catch (_e) {
    /* ignore */
  }
  return {
    url,
    scrollX,
    scrollY,
    capturedAt: Date.now(),
  };
}

export function buildReturnContextFromLocation(location) {
  return buildReturnContextFromParts(
    location?.pathname,
    location?.search,
    location?.hash
  );
}

export function buildReturnContextFromWindow() {
  if (typeof window === "undefined" || !window.location) return null;
  return buildReturnContextFromParts(
    window.location.pathname,
    window.location.search,
    window.location.hash
  );
}

export function normalizeReturnContext(raw) {
  if (!raw || typeof raw !== "object") return null;
  const url = typeof raw.url === "string" ? raw.url.trim() : "";
  if (!isValidReturnUrl(url)) {
    console.log("[callReturn] invalid_return_url rejected=" + url);
    return null;
  }
  const capturedAt = Number(raw.capturedAt) || Date.now();
  if (Date.now() - capturedAt > EXPIRY_MS) {
    return null;
  }
  return {
    url,
    scrollX: Math.max(0, Math.round(Number(raw.scrollX) || 0)),
    scrollY: Math.max(0, Math.round(Number(raw.scrollY) || 0)),
    capturedAt,
  };
}

export function contextFromLegacyReturnTo(returnTo) {
  if (typeof returnTo !== "string" || !returnTo.trim()) return null;
  const url = returnTo.trim().startsWith("/")
    ? returnTo.trim()
    : `/${returnTo.trim()}`;
  return normalizeReturnContext({
    url,
    scrollX: 0,
    scrollY: 0,
    capturedAt: Date.now(),
  });
}

export function saveReturnContext(uid, callId, context) {
  const normalized = normalizeReturnContext(context);
  if (!uid || !callId || !normalized) return false;
  return safeSessionSet(storageKey(uid, callId), JSON.stringify(normalized));
}

export function loadReturnContext(uid, callId) {
  if (!uid || !callId) return null;
  const raw = safeSessionGet(storageKey(uid, callId));
  if (!raw) return null;
  try {
    return normalizeReturnContext(JSON.parse(raw));
  } catch (_e) {
    return null;
  }
}

export function removeReturnContext(uid, callId) {
  if (!uid || !callId) return;
  safeSessionRemove(storageKey(uid, callId));
}

/**
 * Resolve context: navigation state → sessionStorage → legacy returnTo → /home.
 */
export function resolveReturnContext({
  uid,
  callId,
  stateReturnContext,
  legacyReturnTo,
} = {}) {
  const fromState = normalizeReturnContext(stateReturnContext);
  if (fromState) {
    console.log(
      `[callReturn] loaded source=state callId=${callId || ""} url=${fromState.url}`
    );
    return { context: fromState, source: "state" };
  }

  const fromSession = loadReturnContext(uid, callId);
  if (fromSession) {
    console.log(
      `[callReturn] loaded source=session callId=${callId || ""} url=${fromSession.url}`
    );
    return { context: fromSession, source: "session" };
  }

  const fromLegacy = contextFromLegacyReturnTo(legacyReturnTo);
  if (fromLegacy) {
    console.log(
      `[callReturn] loaded source=legacy callId=${callId || ""} url=${fromLegacy.url}`
    );
    return { context: fromLegacy, source: "legacy" };
  }

  console.log(
    `[callReturn] fallback_home reason=no_valid_context callId=${callId || ""}`
  );
  return {
    context: {
      url: CALL_RETURN_FALLBACK_URL,
      scrollX: 0,
      scrollY: 0,
      capturedAt: Date.now(),
    },
    source: "fallback",
  };
}

export function markNativeCallReturnStarted() {
  safeSessionSet(RETURN_AT_KEY, String(Date.now()));
}

export function getMsSinceNativeCallReturn() {
  const raw = safeSessionGet(RETURN_AT_KEY);
  const at = Number(raw);
  if (!Number.isFinite(at) || at <= 0) return null;
  return Math.max(0, Date.now() - at);
}

export function mapCallReturnToastMessage(trigger, reason) {
  const r = String(reason || "").toLowerCase();
  const t = String(trigger || "").toLowerCase();
  if (
    r.includes("insufficient") ||
    r.includes("balance") ||
    t.includes("insufficient")
  ) {
    return "الرصيد غير كافٍ";
  }
  if (
    r.includes("stale") ||
    r.includes("timeout") ||
    r.includes("network") ||
    r.includes("heartbeat") ||
    t.includes("stale")
  ) {
    return "انقطع الاتصال";
  }
  if (
    t === "remote_terminal" ||
    t === "native_remote_terminal" ||
    r.startsWith("terminal_") ||
    r.includes("remote")
  ) {
    return "أنهى الطرف الآخر المكالمة";
  }
  return "انتهت المكالمة";
}
